import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { addDays, format, isSameDay, subDays } from 'date-fns';
import { ChevronLeft, ChevronRight, Clock, MapPin } from 'lucide-react';
import type { Coach, Court } from '@court-sync/shared';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '../ui/Tooltip';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import {
  groupOccurrencesByCoach,
  groupOccurrencesByCourt,
  resolveOccurrenceCoachIds,
  UNASSIGNED_COACH_BUCKET,
  UNASSIGNED_COURT_BUCKET,
  type ScheduleOccurrence,
  type ScheduleOccurrenceSourceType,
} from '@/utils/scheduleOccurrences';
import { computeTargetCoachIds, computeTargetCourtIds } from '@/utils/dayScheduleColumnDrag';
import { ScheduleCurrentTimeIndicator } from '@/components/schedule/ScheduleCurrentTimeIndicator';
import { useScheduleScrollToNow } from '@/hooks/useScheduleScrollToNow';
import {
  getDayScheduleOccurrenceSlotClasses,
  getDayScheduleOccurrenceSlotStyle,
} from '@/utils/scheduleOccurrenceChipProps';
import { useAgeGroupLookups } from '@/queries/ageGroups';
import { formatTime, formatTimeHHMM, minutesToTime, timeToMinutes } from '@/utils/timeFormat';
import { getStackedTimeSlotStyle } from '@/utils/scheduleOverlapLayout';
import { WEEKDAY_SHORT_LABELS } from '@/utils/scheduleWeek';

export type DayCourtDateNavigation = 'calendar' | 'weekday';

const WEEKLY_PX_PER_30_MIN = 50;
const EARLIEST_TIME = 0;
const LATEST_TIME = 24 * 60;

export type DayScheduleColumnAxis = 'court' | 'coach';

export interface DayCourtReschedulePayload {
  occurrence: ScheduleOccurrence;
  startTime: string;
  endTime: string;
  courtIds: string[];
  sourceCourtId?: string;
  columnAxis?: DayScheduleColumnAxis;
  coachIds?: string[];
  sourceCoachId?: string;
}

export interface DayCourtTimeSlotClickPayload {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  /** Set when the user clicked a specific court column; omitted for Unassigned. */
  courtId?: string;
}

export interface DayCourtScheduleViewProps {
  date: Date;
  onDateChange?: (date: Date) => void;
  courts: Court[];
  occurrences: ScheduleOccurrence[];
  academyId?: string;
  editable?: boolean;
  /** Calendar date picker (default) or weekday tabs for planning surfaces. */
  dateNavigation?: DayCourtDateNavigation;
  selectedDayOfWeek?: number;
  onDayOfWeekChange?: (dayOfWeek: number) => void;
  /** When true, date navigation and hints are rendered by the parent toolbar. */
  hideHeader?: boolean;
  /** When set, only occurrences with these source types are shown. */
  sourceTypeFilters?: ScheduleOccurrenceSourceType[];
  /** Court columns (default) or coach columns for day allocation grid. */
  columnAxis?: DayScheduleColumnAxis;
  coaches?: Coach[];
  onOccurrenceClick?: (occurrence: ScheduleOccurrence) => void;
  onOccurrenceReschedule?: (payload: DayCourtReschedulePayload) => void;
  onTimeSlotClick?: (payload: DayCourtTimeSlotClickPayload) => void;
  /** Show current-time line and scroll-to-now (operational calendar only). */
  showCurrentTimeIndicator?: boolean;
}

function columnDroppableId(columnKey: string): string {
  return `column-${columnKey}`;
}

function parseColumnKey(droppableId: string): string {
  return droppableId.replace(/^column-/, '');
}

function DayOccurrenceSlot({
  occurrence,
  style,
  columnKey,
  editable,
  onClick,
  ageGroupById,
}: {
  occurrence: ScheduleOccurrence;
  style: { top: string; height: string; left: string; width: string };
  columnKey: string;
  editable: boolean;
  onClick?: () => void;
  ageGroupById?: Record<string, import('@/utils/lessonSlotColors').AgeGroupCalendarColorLookup>;
}) {
  const dragId = `${occurrence.lessonId}-${occurrence.date.getTime()}-${columnKey}`;
  const isCancelled = occurrence.status === 'cancelled';
  const isClosure = occurrence.sourceType === 'closure';
  const isCourtHire = occurrence.sourceType === 'court_hire';
  const isLessonSlot = occurrence.sourceType === 'lesson';

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    disabled: !editable || isCancelled || isClosure || isCourtHire,
    data: { occurrence, columnKey },
  });

  const slotClassName = getDayScheduleOccurrenceSlotClasses(occurrence, {
    ageGroupById,
    editable: editable && isLessonSlot,
    onClick: !!onClick,
    isDragging,
  });

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <div
          ref={setNodeRef}
          {...(editable && isLessonSlot && !isCancelled ? { ...listeners, ...attributes } : {})}
          data-lesson-slot="true"
          onClick={() => {
            if (onClick && !isCancelled) onClick();
          }}
          className={slotClassName}
          style={{
            top: style.top,
            height: style.height,
            left: style.left,
            width: style.width,
            ...getDayScheduleOccurrenceSlotStyle(occurrence, { ageGroupById }),
          }}
        >
          <div className="font-medium mb-0.5 flex items-center gap-1 min-w-0 shrink-0">
            <span className="min-w-0 truncate">{occurrence.lessonName}</span>
            {occurrence.hasCourtOverride ? (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 shrink-0">
                Court
              </Badge>
            ) : null}
          </div>
          <div className="text-xs opacity-75 truncate min-w-0 shrink-0">
            {formatTimeHHMM(occurrence.startTime)} - {formatTimeHHMM(occurrence.endTime)}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-sm">
        <p className="font-semibold">{occurrence.lessonName}</p>
        <p>
          {formatTimeHHMM(occurrence.startTime)} – {formatTimeHHMM(occurrence.endTime)}
        </p>
        {occurrence.courtLabel ? <p>Courts: {occurrence.courtLabel}</p> : null}
        {editable && isLessonSlot ? (
          <p className="text-xs text-muted-foreground mt-1">Drag to move time</p>
        ) : null}
      </TooltipContent>
    </TooltipRoot>
  );
}

function DroppableCourtColumn({
  columnKey,
  columnLabel,
  dropTarget,
  hoverTarget,
  onTimeSlotClick,
  onTimeSlotHover,
  onTimeSlotHoverLeave,
  children,
  editable: _editable,
}: {
  columnKey: string;
  columnLabel: string;
  dropTarget: { columnKey: string; topPercent: number; heightPercent: number } | null;
  hoverTarget: { columnKey: string; topPercent: number; heightPercent: number } | null;
  onTimeSlotClick?: (event: React.MouseEvent<HTMLDivElement>, columnKey: string) => void;
  onTimeSlotHover?: (event: React.MouseEvent<HTMLDivElement>, columnKey: string) => void;
  onTimeSlotHoverLeave?: () => void;
  children: React.ReactNode;
  editable: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnDroppableId(columnKey) });
  const showTarget = dropTarget?.columnKey === columnKey;
  const showHoverTarget = hoverTarget?.columnKey === columnKey;

  return (
    <div
      ref={setNodeRef}
      onClick={(event) => {
        if (!onTimeSlotClick) return;
        const target = event.target as HTMLElement;
        if (target.closest('[data-lesson-slot="true"]')) return;
        onTimeSlotClick(event, columnKey);
      }}
      onMouseMove={(event) => onTimeSlotHover?.(event, columnKey)}
      onMouseLeave={onTimeSlotHoverLeave}
      className={`border-r border-gray-200 dark:border-gray-700 last:border-r-0 relative bg-white dark:bg-gray-900 transition-colors min-w-[6rem] ${
        isOver ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
      } ${onTimeSlotClick ? 'cursor-cell' : ''}`}
      aria-label={columnLabel}
    >
      {showHoverTarget && hoverTarget ? (
        <div
          className="absolute left-1 right-1 rounded border border-blue-400/70 bg-blue-200/20 dark:bg-blue-500/15 pointer-events-none z-10"
          style={{
            top: `${hoverTarget.topPercent}%`,
            height: `${hoverTarget.heightPercent}%`,
            minHeight: '24px',
          }}
          aria-hidden
        />
      ) : null}
      {showTarget && dropTarget ? (
        <div
          className="absolute left-1 right-1 rounded border-2 border-dashed border-blue-500 bg-transparent pointer-events-none z-20"
          style={{
            top: `${dropTarget.topPercent}%`,
            height: `${dropTarget.heightPercent}%`,
            minHeight: '32px',
          }}
          aria-hidden
        />
      ) : null}
      {children}
    </div>
  );
}

export function DayCourtScheduleView({
  date,
  onDateChange,
  courts,
  occurrences,
  academyId,
  editable = false,
  dateNavigation = 'calendar',
  selectedDayOfWeek = date.getDay(),
  onDayOfWeekChange,
  hideHeader = false,
  sourceTypeFilters,
  columnAxis = 'court',
  coaches = [],
  onOccurrenceClick,
  onOccurrenceReschedule,
  onTimeSlotClick,
  showCurrentTimeIndicator = true,
}: DayCourtScheduleViewProps) {
  const { ageGroupById } = useAgeGroupLookups(academyId);
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const [activeOccurrence, setActiveOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [activeSourceColumn, setActiveSourceColumn] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    columnKey: string;
    topPercent: number;
    heightPercent: number;
  } | null>(null);
  const [hoverTarget, setHoverTarget] = useState<{
    columnKey: string;
    topPercent: number;
    heightPercent: number;
  } | null>(null);
  const lastDropYRef = useRef<{
    centerY: number;
    overRect: { top: number; height: number };
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const isCoachAxis = columnAxis === 'coach';
  const unassignedBucket = isCoachAxis ? UNASSIGNED_COACH_BUCKET : UNASSIGNED_COURT_BUCKET;
  const activeCoaches = useMemo(
    () => coaches.filter((coach) => coach.isActive !== false),
    [coaches]
  );

  const columnKeys = useMemo(() => {
    if (isCoachAxis) {
      return [UNASSIGNED_COACH_BUCKET, ...activeCoaches.map((coach) => coach.id)];
    }
    return [UNASSIGNED_COURT_BUCKET, ...courts.map((court) => court.id)];
  }, [activeCoaches, courts, isCoachAxis]);

  const columnLabels = useMemo(() => {
    const labels = new Map<string, string>();
    labels.set(unassignedBucket, 'Unassigned');
    if (isCoachAxis) {
      for (const coach of activeCoaches) {
        labels.set(coach.id, `${coach.firstName} ${coach.lastName}`.trim());
      }
    } else {
      for (const court of courts) {
        labels.set(court.id, court.name);
      }
    }
    return labels;
  }, [activeCoaches, courts, isCoachAxis, unassignedBucket]);

  const filteredOccurrences = useMemo(() => {
    if (!sourceTypeFilters?.length) return occurrences;
    const allowed = new Set(sourceTypeFilters);
    return occurrences.filter((occ) => allowed.has(occ.sourceType));
  }, [occurrences, sourceTypeFilters]);

  const occurrencesByColumn = useMemo(() => {
    if (isCoachAxis) {
      return groupOccurrencesByCoach(
        filteredOccurrences,
        activeCoaches.map((coach) => coach.id)
      );
    }
    return groupOccurrencesByCourt(filteredOccurrences, courts.map((court) => court.id));
  }, [activeCoaches, courts, filteredOccurrences, isCoachAxis]);

  const effectiveTimeSlotClick = isCoachAxis ? undefined : onTimeSlotClick;

  const timeSlots = useMemo(() => {
    const s: number[] = [];
    for (let m = 0; m < 24 * 60; m += 30) s.push(m);
    return s;
  }, []);

  const totalRange = LATEST_TIME - EARLIEST_TIME;
  const isToday = isSameDay(date, new Date());
  const showNowIndicator = showCurrentTimeIndicator && isToday;

  const fallbackAnchorMinutes = useMemo(() => {
    let min: number | null = null;
    for (const occ of filteredOccurrences) {
      const start = timeToMinutes(occ.startTime);
      if (min === null || start < min) min = start;
    }
    return min ?? 8 * 60;
  }, [filteredOccurrences]);

  useScheduleScrollToNow({
    scrollRef: scheduleScrollRef,
    enabled: showNowIndicator,
    earliestTime: EARLIEST_TIME,
    latestTime: LATEST_TIME,
    pxPerSlot: WEEKLY_PX_PER_30_MIN,
    fallbackAnchorMinutes,
    deps: [date, columnAxis],
  });

  const resolvedDayOfWeek =
    dateNavigation === 'weekday' ? selectedDayOfWeek : date.getDay();

  const getSnappedSlotStartMinutes = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.height <= 0) return null;
    const relativeY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    const minutesFromTop = (relativeY / rect.height) * totalRange;
    const slotSizeMinutes = 30;
    const snappedStartMinutes =
      EARLIEST_TIME + Math.floor(minutesFromTop / slotSizeMinutes) * slotSizeMinutes;
    return Math.max(0, Math.min(24 * 60 - slotSizeMinutes, snappedStartMinutes));
  }, [totalRange]);

  const handleTimeSlotClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, columnKey: string) => {
      if (!effectiveTimeSlotClick) return;
      const snappedStartMinutes = getSnappedSlotStartMinutes(event);
      if (snappedStartMinutes === null) return;
      const defaultDurationMinutes = 60;
      const endMinutes = Math.min(snappedStartMinutes + defaultDurationMinutes, 24 * 60 - 1);

      effectiveTimeSlotClick({
        dayOfWeek: resolvedDayOfWeek,
        startTime: minutesToTime(snappedStartMinutes),
        endTime: minutesToTime(endMinutes),
        courtId: columnKey === UNASSIGNED_COURT_BUCKET ? undefined : columnKey,
      });
    },
    [effectiveTimeSlotClick, getSnappedSlotStartMinutes, resolvedDayOfWeek]
  );

  const handleTimeSlotHover = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, columnKey: string) => {
      if (!effectiveTimeSlotClick) return;
      if (activeOccurrence) return;
      const target = event.target as HTMLElement;
      if (target.closest('[data-lesson-slot="true"]')) {
        setHoverTarget(null);
        return;
      }
      const snappedStartMinutes = getSnappedSlotStartMinutes(event);
      if (snappedStartMinutes === null) return;
      const durationMinutes = 60;
      const topPercent = ((snappedStartMinutes - EARLIEST_TIME) / totalRange) * 100;
      const heightPercent = (durationMinutes / totalRange) * 100;

      setHoverTarget({ columnKey, topPercent, heightPercent });
    },
    [activeOccurrence, effectiveTimeSlotClick, getSnappedSlotStartMinutes, totalRange]
  );

  const handleTimeSlotHoverLeave = useCallback(() => {
    setHoverTarget(null);
  }, []);

  const getOccurrenceStyle = useCallback(
    (occ: ScheduleOccurrence, columnOccurrences: ScheduleOccurrence[]) =>
      getStackedTimeSlotStyle(
        occ,
        columnOccurrences,
        (o) => `${o.lessonId}-${o.date.getTime()}`,
        EARLIEST_TIME,
        LATEST_TIME
      ),
    []
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    lastDropYRef.current = null;
    setDropTarget(null);
    const data = event.active.data.current as
      | { occurrence: ScheduleOccurrence; columnKey: string }
      | undefined;
    if (data?.occurrence) {
      setActiveOccurrence(data.occurrence);
      setActiveSourceColumn(data.columnKey);
    }
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { over, active } = event;
      if (!over?.rect) {
        setDropTarget(null);
        return;
      }
      const translated = (
        active as unknown as {
          rect?: { current?: { translated?: { top: number; height: number } } };
        }
      ).rect?.current?.translated;
      if (!translated) return;
      const centerY = translated.top + translated.height / 2;
      const overRect = { top: over.rect.top, height: over.rect.height };
      lastDropYRef.current = { centerY, overRect };
      const overId = String(over.id);
      if (!overId.startsWith('column-')) return;
      const columnKey = parseColumnKey(overId);
      const relativeY = centerY - overRect.top;
      const dropMinutes = EARLIEST_TIME + (relativeY / overRect.height) * totalRange;
      const snappedMinutes = Math.max(
        0,
        Math.min(24 * 60 - 30, Math.round(dropMinutes / 30) * 30)
      );
      const activeData = active.data.current as { occurrence: ScheduleOccurrence } | undefined;
      const duration = activeData?.occurrence
        ? timeToMinutes(activeData.occurrence.endTime) -
          timeToMinutes(activeData.occurrence.startTime)
        : 60;
      const durationMinutes = Math.min(duration, 24 * 60 - snappedMinutes);
      const topPercent = ((snappedMinutes - EARLIEST_TIME) / totalRange) * 100;
      const heightPercent = (durationMinutes / totalRange) * 100;
      setDropTarget({ columnKey, topPercent, heightPercent });
    },
    [totalRange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveOccurrence(null);
      setDropTarget(null);
      const sourceColumn = activeSourceColumn;
      setActiveSourceColumn(null);
      const { active, over } = event;
      if (!over || !onOccurrenceReschedule) return;
      const data = active.data.current as
        | { occurrence: ScheduleOccurrence; columnKey: string }
        | undefined;
      if (!data?.occurrence) return;
      const overId = String(over.id);
      if (!overId.startsWith('column-')) return;

      const targetColumnKey = parseColumnKey(overId);
      const cached = lastDropYRef.current;
      lastDropYRef.current = null;
      const droppableRect = over.rect ? { top: over.rect.top, height: over.rect.height } : cached?.overRect;
      if (!droppableRect) return;

      const centerY = cached?.centerY ?? droppableRect.top + droppableRect.height / 2;
      const relativeY = centerY - droppableRect.top;
      const dropMinutes = EARLIEST_TIME + (relativeY / droppableRect.height) * totalRange;
      const snappedMinutes = Math.max(
        0,
        Math.min(24 * 60 - 30, Math.round(dropMinutes / 30) * 30)
      );
      const duration =
        timeToMinutes(data.occurrence.endTime) - timeToMinutes(data.occurrence.startTime);
      let endMinutes = snappedMinutes + duration;
      if (endMinutes > 24 * 60) endMinutes = 24 * 60;
      const startTime = minutesToTime(snappedMinutes);
      const endTime = minutesToTime(endMinutes);

      if (isCoachAxis) {
        const currentCoachIds = resolveOccurrenceCoachIds(data.occurrence);
        const sourceCoachId =
          sourceColumn && sourceColumn !== UNASSIGNED_COACH_BUCKET ? sourceColumn : undefined;
        const targetCoachIds = computeTargetCoachIds(
          currentCoachIds,
          targetColumnKey,
          sourceCoachId
        );
        onOccurrenceReschedule({
          occurrence: data.occurrence,
          startTime,
          endTime,
          courtIds: data.occurrence.resolvedCourtIds ?? [],
          columnAxis: 'coach',
          coachIds: targetCoachIds,
          sourceCoachId,
        });
        return;
      }

      const currentCourtIds = data.occurrence.resolvedCourtIds ?? [];
      const sourceCourtId =
        sourceColumn && sourceColumn !== UNASSIGNED_COURT_BUCKET ? sourceColumn : undefined;
      const targetCourtIds = computeTargetCourtIds(
        currentCourtIds,
        targetColumnKey,
        sourceCourtId
      );

      onOccurrenceReschedule({
        occurrence: data.occurrence,
        startTime,
        endTime,
        courtIds: targetCourtIds,
        sourceCourtId,
        columnAxis: 'court',
      });
    },
    [activeSourceColumn, isCoachAxis, onOccurrenceReschedule, totalRange]
  );

  if (isCoachAxis && activeCoaches.length === 0) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={MapPin} />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No active coaches
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Add coaches in Staff to use the coach day view.
        </p>
      </DashboardEmptyStateCard>
    );
  }

  if (!isCoachAxis && courts.length === 0) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={MapPin} />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No courts at this location
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Add courts in Court Management to use the day view allocation grid.
        </p>
      </DashboardEmptyStateCard>
    );
  }

  const gridTemplateColumns = `2.75rem repeat(${columnKeys.length}, minmax(6rem, 1fr))`;
  const gridMinHeight = `${(totalRange / 30) * WEEKLY_PX_PER_30_MIN}px`;

  const gridBody = (
    <div className="inline-block min-w-full">
      <div className="flex h-[min(70vh,720px)] min-h-[220px] flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div
          ref={scheduleScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-y-contain [scrollbar-gutter:stable]"
        >
          <div
            className="sticky top-0 z-30 shrink-0 border-b border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 grid"
            style={{ gridTemplateColumns }}
          >
            <div className="sticky left-0 z-40 border-r border-gray-200 bg-gray-100 p-2 dark:border-gray-700 dark:bg-gray-800">
              <div className="font-semibold text-gray-900 dark:text-white text-sm">Time</div>
            </div>
            {columnKeys.map((columnKey) => (
              <div
                key={columnKey}
                className="border-r border-gray-200 bg-gray-100 p-2 text-center last:border-r-0 dark:border-gray-700 dark:bg-gray-800"
              >
                <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm truncate">
                  {columnLabels.get(columnKey) ?? columnKey}
                </div>
              </div>
            ))}
          </div>

          <div className="relative grid" style={{ gridTemplateColumns, minHeight: gridMinHeight }}>
            {showNowIndicator ? (
              <div className="pointer-events-none absolute inset-0 z-30" aria-hidden>
                <ScheduleCurrentTimeIndicator
                  earliestTime={EARLIEST_TIME}
                  latestTime={LATEST_TIME}
                  showDot
                />
              </div>
            ) : null}
            <div className="border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky left-0 z-10 relative">
              {timeSlots.map((slotMinutes, index) => {
                const pos = ((slotMinutes - EARLIEST_TIME) / totalRange) * 100;
                return (
                  <React.Fragment key={slotMinutes}>
                    <div
                      className="absolute left-0 right-0 border-b border-gray-200 dark:border-gray-700"
                      style={{ top: `${pos}%` }}
                    />
                    {index % 2 === 0 ? (
                      <div
                        className="absolute left-0 right-0 px-1 pt-0.5 text-xs text-gray-600 dark:text-gray-400"
                        style={{ top: `${pos}%` }}
                      >
                        {formatTime(minutesToTime(slotMinutes))}
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>

            {columnKeys.map((columnKey) => {
              const columnOccurrences = occurrencesByColumn.get(columnKey) ?? [];
              return (
                <DroppableCourtColumn
                  key={columnKey}
                  columnKey={columnKey}
                  columnLabel={columnLabels.get(columnKey) ?? columnKey}
                  dropTarget={dropTarget}
                  hoverTarget={hoverTarget}
                  onTimeSlotClick={
                    effectiveTimeSlotClick
                      ? (event, key) => handleTimeSlotClick(event, key)
                      : undefined
                  }
                  onTimeSlotHover={
                    effectiveTimeSlotClick
                      ? (event, key) => handleTimeSlotHover(event, key)
                      : undefined
                  }
                  onTimeSlotHoverLeave={
                    effectiveTimeSlotClick ? handleTimeSlotHoverLeave : undefined
                  }
                  editable={editable}
                >
                  {timeSlots.map((slotMinutes) => {
                    const pos = ((slotMinutes - EARLIEST_TIME) / totalRange) * 100;
                    return (
                      <div
                        key={slotMinutes}
                        className="absolute left-0 right-0 border-b border-gray-100 dark:border-gray-800"
                        style={{ top: `${pos}%` }}
                      />
                    );
                  })}
                  {columnOccurrences.map((occ) => (
                    <DayOccurrenceSlot
                      key={`${occ.lessonId}-${occ.date.getTime()}-${columnKey}`}
                      occurrence={occ}
                      columnKey={columnKey}
                      style={getOccurrenceStyle(occ, columnOccurrences)}
                      editable={editable}
                      onClick={() => onOccurrenceClick?.(occ)}
                      ageGroupById={ageGroupById}
                    />
                  ))}
                </DroppableCourtColumn>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full space-y-4">
        {!hideHeader ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {dateNavigation === 'weekday' ? (
            <div
              className="flex flex-wrap items-center gap-1"
              role="group"
              aria-label="Day of week"
            >
              {WEEKDAY_SHORT_LABELS.map((label, dayIndex) => (
                <Button
                  key={label}
                  type="button"
                  size="sm"
                  variant={selectedDayOfWeek === dayIndex ? 'default' : 'outline'}
                  className="h-8 min-w-[2.5rem] px-2"
                  aria-pressed={selectedDayOfWeek === dayIndex}
                  onClick={() => onDayOfWeekChange?.(dayIndex)}
                >
                  {label}
                </Button>
              ))}
            </div>
          ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDateChange?.(subDays(date, 1))}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <input
              type="date"
              value={format(date, 'yyyy-MM-dd')}
              onChange={(e) => {
                if (e.target.value && onDateChange) {
                  const [y, m, d] = e.target.value.split('-').map(Number);
                  onDateChange(new Date(y, m - 1, d));
                }
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Select day"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDateChange?.(addDays(date, 1))}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-gray-900 dark:text-white hidden sm:inline">
              {format(date, 'EEEE, MMM d, yyyy')}
            </span>
          </div>
          )}
          {editable || effectiveTimeSlotClick ? (
            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <Clock className="h-4 w-4" aria-hidden />
              <span>
                {effectiveTimeSlotClick ? 'Click empty slot to create' : null}
                {effectiveTimeSlotClick && editable ? ' • ' : null}
                {editable
                  ? isCoachAxis
                    ? 'Drag to move coach or time'
                    : 'Drag to move court or time'
                  : null}
              </span>
            </div>
          ) : null}
        </div>
        ) : null}

        {editable ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          >
            {gridBody}
            <DragOverlay dropAnimation={null}>
              {activeOccurrence ? (
                <div className="rounded border-2 border-blue-400 bg-white p-2 text-xs shadow-xl dark:bg-gray-800 min-w-[120px]">
                  <div className="font-medium truncate">{activeOccurrence.lessonName}</div>
                  <div className="opacity-75">
                    {formatTimeHHMM(activeOccurrence.startTime)} -{' '}
                    {formatTimeHHMM(activeOccurrence.endTime)}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          gridBody
        )}
      </div>
    </TooltipProvider>
  );
}
